import { useMemo, useState } from 'react'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney, fromMajor, money, toMajor, type CurrencyCode } from '@/lib/money'
import { cn } from '@/lib/cn'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

const SPREAD = 0.006
const MID: Partial<Record<CurrencyCode, number>> = { USD: 1, EUR: 0.9259, MXN: 17.42, BRL: 5.34, COP: 3920, BOB: 6.91 }

export function ConvertScreen() {
  const repository = useRepository()
  const accounts = useQuery(() => repository.listAccounts(), [repository])
  const [amount, setAmount] = useState('5000')
  const [target, setTarget] = useState<CurrencyCode>('EUR')

  const options = accounts.data?.filter((account) => account.currency !== 'USD' && MID[account.currency]) ?? []

  const result = useMemo(() => {
    const parsed = Number.parseFloat(amount.replace(/,/g, ''))
    const from = fromMajor(Number.isFinite(parsed) && parsed > 0 ? parsed : 0, 'USD')
    const mid = MID[target] ?? 1
    const rate = mid * (1 - SPREAD)
    const fee = money(Math.round(from.minorUnits * SPREAD), 'USD')
    return { from, rate, fee, to: fromMajor(toMajor(from) * rate, target) }
  }, [amount, target])

  return (
    <>
      <PageHeader
        title="Convert"
        subtitle="One spread, shown in the rate. No hidden margin and no separate conversion fee."
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Stack gap={5}>
              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  From
                </Text>
                <div className={styles.amountField}>
                  <Text variant="eyebrow" tone="secondary">
                    US Dollar account · $18,240.50 available
                  </Text>
                  <div className={styles.amountRow}>
                    <CurrencyBadge currency="USD" size={36} />
                    <input
                      className={styles.amountInput}
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value.replace(/[^\d.,]/g, ''))}
                      aria-label="Amount to convert"
                    />
                    <Text variant="subtitle" weight="semibold" tone="secondary">
                      USD
                    </Text>
                  </div>
                </div>
              </Stack>

              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  To
                </Text>
                <div className={styles.corridors}>
                  {options.map((account) => (
                    <button
                      key={account.id}
                      type="button"
                      className={cn(styles.corridor, account.currency === target && styles.corridorActive)}
                      onClick={() => setTarget(account.currency)}
                      aria-pressed={account.currency === target}
                    >
                      <CurrencyBadge currency={account.currency} size={28} />
                      <Stack gap={0}>
                        <Text variant="caption" weight="semibold">
                          {account.currency}
                        </Text>
                        <Text variant="eyebrow" tone="secondary">
                          {account.label}
                        </Text>
                      </Stack>
                    </button>
                  ))}
                </div>
              </Stack>
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                You get
              </Text>
              <Pill tone="accent">Instant</Pill>
            </Card.Header>
            <Stack gap={1} className="pb-mt-4">
              <Text as="p" variant="display" weight="bold" tone="accent" numeric>
                {formatMoney(result.to, { compactDecimals: true })}
              </Text>
              <Text variant="caption" tone="onDarkMuted" numeric>
                1 USD = {result.rate.toFixed(target === 'COP' ? 0 : 4)} {target}
              </Text>
            </Stack>

            <div className={checkout.rule} />

            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Amount
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark" numeric>
                {formatMoney(result.from)}
              </Text>
            </div>
            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Spread · 0.6%
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark" numeric>
                {formatMoney(result.fee)}
              </Text>
            </div>
            <div className={checkout.line}>
              <Text variant="caption" tone="onDarkMuted">
                Conversion fee
              </Text>
              <Text variant="caption" weight="semibold" tone="accent">
                None
              </Text>
            </div>

            <Card.Footer>
              <Button size="lg" block iconEnd="arrow_forward" to="/convert/review">
                Convert now
              </Button>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="light">
            <Stack direction="row" align="center" gap={3}>
              <Icon name="info" size={20} />
              <Text variant="caption" tone="secondary">
                The rate above already includes our 0.6% spread — there is nothing else added at
                confirmation. Rates move, so the quote is held for 30 seconds once you tap convert.
              </Text>
            </Stack>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
